import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@apollo/orbit';
import moment from 'moment';
import React from 'react';

import { FieldDefinition } from 'src/app/graph/insights/fieldPage/summary/fieldDefinition/FieldDefinition';
import { FieldPageDescription } from 'src/app/graph/insights/fieldPage/summary/fieldPageDescription/FieldPageDescription';
import { FieldPageSDLButton } from 'src/app/graph/insights/fieldPage/summary/fieldPageSDL/fieldPageSDLButton/FieldPageSDLButton';
import { FieldPageSubgraphs } from 'src/app/graph/insights/fieldPage/summary/FieldPageSubgraphs';
import { FieldPageTags } from 'src/app/graph/insights/fieldPage/summary/fieldPageTags/FieldPageTags';
import { FieldSummaryEmptyText } from 'src/app/graph/insights/fieldPage/summary/FieldSummaryEmptyText';
import { FieldSummarySection } from 'src/app/graph/insights/fieldPage/summary/FieldSummarySection';
import { FieldInsightsFilter } from 'src/app/graph/insights/fieldPage/usage/FieldInsightsFilter';
import { FieldPageCharts } from 'src/app/graph/insights/fieldPage/usage/FieldPageCharts';
import { FieldPageClientMetrics } from 'src/app/graph/insights/fieldPage/usage/fieldPageClientMetrics/FieldPageClientMetrics';
import { NoClientMetrics } from 'src/app/graph/insights/fieldPage/usage/noClientMetrics/NoClientMetrics';
import {
  isCurrentFieldResultError,
  useCurrentSchemaCoordinate,
} from 'src/app/graph/insights/fieldPage/useCurrentSchemaCoordinate';
import { InsightsExpandButton } from 'src/app/graph/insights/InsightsExpandButton';
import { useInsightsL3NavState } from 'src/app/graph/operationsPage/insightsL3Nav/useInsightsL3NavState';
import { fieldRouteConfig } from 'src/app/graph/routes';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { InsightsEnforcementWarning } from 'src/components/insightsEnforcementWarning/InsightsEnforcementWarning';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';

import { friendlyCoordinateKind } from './shared';
import { FieldPageClientHeading } from './usage/fieldPageClientMetrics/FieldPageClientHeading';

interface Props {
  __test?: {
    summaryLoading?: boolean;
    metricsLoading?: boolean;
    boundsLoading?: boolean;
    error?: boolean;
    fieldPageNoUsage?: boolean;
    fieldPageNoMetrics?: boolean;
    noVariantMetrics?: boolean;
  };
}

const addOverFormatting = (earliestRetentionTime: Date) => () => {
  const duration = moment.duration(
    moment().utc().diff(moment(earliestRetentionTime)),
  );
  return `Over ${duration.humanize()} ago`;
};

const addWithinHourFormatting = () => 'Within the last hour';

const isWithinHour = (time?: Date) =>
  time && moment(time) > moment().subtract(1, 'hours');

export const timeSeenFormatter = (
  firstOrLastSeen?: Date,
  earliestRetentionTime?: Date,
) => {
  if (
    firstOrLastSeen &&
    earliestRetentionTime &&
    firstOrLastSeen < earliestRetentionTime
  ) {
    return addOverFormatting(earliestRetentionTime);
  }

  // If our druid data is delayed we might not have accurate timestamps, so we
  // can just show "within the last hour" instead of an exact time
  if (isWithinHour(firstOrLastSeen)) {
    return addWithinHourFormatting;
  }

  return null;
};

const definitionHeadingByCoordinateKind = new Map<CoordinateKind, string>([
  [CoordinateKind.ENUM_VALUE, 'Enum definition'],
  [CoordinateKind.INPUT_OBJECT_FIELD, 'Input field definition'],
  [CoordinateKind.OBJECT_FIELD, 'Field definition'],
]);

const FieldNotFound = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => (
  <NotFound
    image="satellite"
    title="Oops, no sign of life here..."
    subtitle={
      <>
        <div className="mb-8">
          The {friendlyCoordinateKind.get(coordinateKind)} you are looking for
          could not be found.
        </div>
      </>
    }
  />
);

export const FieldMainContent = (props: Props) => {
  const hideClientAndOperationStats = useLDFlag(
    'pulsar-disable-stats-for-field-usage',
  );

  const currentField = useCurrentSchemaCoordinate();
  const { isOpen, onOpen } = useInsightsL3NavState();
  const { coordinateKind = CoordinateKind.OBJECT_FIELD } =
    useRouteParams(fieldRouteConfig);

  if (isCurrentFieldResultError(currentField) || props?.__test?.error) {
    return <FieldNotFound coordinateKind={coordinateKind} />;
  }

  const {
    field,
    fieldName,
    typeName,
    parentType,
    isRootType,
    summaryLoading,
    metricsLoading,
    boundsLoading,
    graphId,
    schemaInfo,
    variant,
    fieldMetrics,
    hasActiveClientFilters,
    timeRange,
    getSubgraphs,
    accessBounds,
    emptyState,
  } = currentField;

  const fieldSummaryLoading = Boolean(
    summaryLoading || props?.__test?.summaryLoading,
  );
  const fieldMetricsLoading = Boolean(
    metricsLoading || props?.__test?.metricsLoading,
  );
  const boundsMetricsLoading = Boolean(
    boundsLoading || props?.__test?.boundsLoading,
  );

  const firstSeenFormatter = timeSeenFormatter(
    accessBounds.firstSeen,
    accessBounds.earliestRetentionTime,
  );
  const lastSeenFormatter = timeSeenFormatter(
    accessBounds.lastSeen,
    accessBounds.earliestRetentionTime,
  );

  return (
    <div className="bg-primary px-8 pb-24">
      <div className="sticky top-0 z-10 bg-primary pt-4">
        <PageTitle
          title={`${typeName}.${fieldName}`}
          className="border-b-0 px-[0px]"
          leftAdornment={
            !isOpen ? (
              <Tooltip label="Expand">
                <InsightsExpandButton onOpen={onOpen} />
              </Tooltip>
            ) : null
          }
          rightSide={
            !emptyState?.fieldHasUsage && !fieldMetricsLoading ? null : (
              <FieldInsightsFilter
                ranges={timeRange.ranges}
                maxRangeInDays={timeRange.maxRangeInDays}
                fastModeRange={timeRange.fastModeRange}
              />
            )
          }
        >
          <div className="truncate">
            <span className="font-normal">{typeName}.</span>
            <span>{fieldName}</span>
          </div>
        </PageTitle>
        <div className="text-lg font-normal">
          {fieldSummaryLoading ? (
            <ShimmerLoading className="my-2 w-52" />
          ) : (
            <FieldPageDescription
              description={field?.description}
              coordinateKind={coordinateKind}
            />
          )}
        </div>
        <InsightsEnforcementWarning />
      </div>
      <div className="@container">
        <section className="mt-4 flex flex-col gap-8 rounded border border-primary bg-primary p-4 @6xl:flex-row">
          <FieldSummarySection
            className="min-w-max"
            title={
              <Tooltip
                placement="bottom-start"
                label={
                  <span>
                    Reflects the earliest time that <em>any</em> clients have
                    used this {friendlyCoordinateKind.get(coordinateKind)}. Note
                    that accuracy is limited to the retention limits of your
                    plan, and times do not reflect any client filters selected.
                  </span>
                }
              >
                <span className="inline-flex items-center">
                  Date First Seen Traffic
                  <IconInfo className="ml-2 text-secondary" />
                </span>
              </Tooltip>
            }
          >
            {boundsMetricsLoading ? (
              <ShimmerLoading className="mt-2 w-40" />
            ) : accessBounds.firstSeen ? (
              <RelativeTimestamp
                format={firstSeenFormatter || 'fromnow'}
                value={new Date(accessBounds.firstSeen ?? 0)}
                withTooltip={!firstSeenFormatter}
              />
            ) : emptyState?.showEmptyState ? (
              <FieldSummaryEmptyText
                message={`No traffic seen for this ${friendlyCoordinateKind.get(
                  coordinateKind,
                )}.`}
              />
            ) : null}
          </FieldSummarySection>

          <FieldSummarySection
            className="min-w-max"
            title={
              <Tooltip
                placement="bottom-start"
                label={
                  <span>
                    Reflects the last time that <em>any</em> clients have used
                    this {friendlyCoordinateKind.get(coordinateKind)}. Note that
                    accuracy is limited to the retention limits of your plan,
                    and times do not reflect any client filters selected.
                  </span>
                }
              >
                <span className="inline-flex items-center">
                  Date Last Seen Traffic
                  <IconInfo className="ml-2 text-secondary" />
                </span>
              </Tooltip>
            }
          >
            {boundsMetricsLoading ? (
              <ShimmerLoading className="mt-2 w-40" />
            ) : emptyState?.fieldHasUsage ? (
              <RelativeTimestamp
                format={lastSeenFormatter || 'fromnow'}
                value={new Date(accessBounds.lastSeen ?? 0)}
                withTooltip={!lastSeenFormatter}
              />
            ) : (
              <FieldSummaryEmptyText
                message={`No traffic seen for this ${friendlyCoordinateKind.get(
                  coordinateKind,
                )}.`}
              />
            )}
          </FieldSummarySection>

          <FieldSummarySection className="min-w-max" title="Tags">
            {fieldSummaryLoading ? (
              <ShimmerLoading className="mt-2 w-40" />
            ) : field ? (
              <FieldPageTags field={field} />
            ) : null}
          </FieldSummarySection>

          <FieldSummarySection
            title={
              <Tooltip
                interactive
                placement="bottom-start"
                label={
                  <span className="font-normal normal-case">
                    List of subgraphs that provide this{' '}
                    {friendlyCoordinateKind.get(coordinateKind)}. Learn more
                    about using subgraphs{' '}
                    <ClickableText
                      className="font-semibold underline"
                      as="a"
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://www.apollographql.com/docs/federation"
                    >
                      here
                    </ClickableText>
                    .
                  </span>
                }
              >
                <span className="inline-flex items-center">
                  Subgraphs
                  <IconInfo className="ml-2 text-secondary" />
                </span>
              </Tooltip>
            }
          >
            {fieldSummaryLoading ? (
              <ShimmerLoading className="mt-2 w-36" />
            ) : field ? (
              <FieldPageSubgraphs
                field={field}
                parentType={parentType}
                variant={variant}
                graphId={graphId}
                getSubgraphs={getSubgraphs}
              />
            ) : null}
          </FieldSummarySection>
        </section>

        <div className="mt-6">
          <div className="grid grid-cols-1 gap-6 @6xl:grid-cols-3">
            <Card
              isDivided
              className="max-h-[480px] flex-1 overflow-hidden @6xl:col-span-1"
            >
              <CardHeader>
                <CardTitle>
                  {definitionHeadingByCoordinateKind.get(coordinateKind)}
                </CardTitle>
                <CardDescription>As defined in the API schema.</CardDescription>
                {!fieldSummaryLoading && field && (
                  <FieldPageSDLButton schemaInfo={schemaInfo} />
                )}
              </CardHeader>
              <CardBody className="bg-secondary">
                <FieldDefinition
                  field={field}
                  isRootType={isRootType}
                  loading={summaryLoading}
                  coordinateKind={coordinateKind}
                />
              </CardBody>
            </Card>

            <Card
              isDivided
              className="h-full max-h-[480px] flex-2 overflow-hidden p-0 @6xl:col-span-2"
            >
              <CardHeader>
                <FieldPageClientHeading
                  showEmptyState={
                    hideClientAndOperationStats || emptyState?.showEmptyState
                  }
                  loading={fieldMetricsLoading}
                  fieldMetrics={fieldMetrics}
                />
              </CardHeader>
              <CardBody className="!p-0">
                <section className="flex h-full flex-col">
                  {hideClientAndOperationStats ? (
                    <NoClientMetrics
                      accessBounds={accessBounds}
                      emptyState={{ clientAndOperationMetricsDisabled: true }}
                    />
                  ) : emptyState?.showEmptyState ? (
                    <NoClientMetrics
                      accessBounds={accessBounds}
                      emptyState={emptyState}
                    />
                  ) : (
                    <FieldPageClientMetrics
                      fieldMetrics={fieldMetrics}
                      loading={fieldMetricsLoading}
                    />
                  )}
                </section>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      {!emptyState?.showEmptyState && (
        <div className="pt-8">
          <FieldPageCharts
            loading={fieldMetricsLoading}
            fieldMetrics={fieldMetrics}
            resolution={timeRange.resolution}
            showClientFiltersWarning={hasActiveClientFilters}
            coordinateKind={coordinateKind}
          />
        </div>
      )}
    </div>
  );
};
